import React, {
  useEffect, useContext, useMemo, useState, useRef
} from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

// Importing workbooks player component
import WorkbooksPlayer from 'workbooks-player';

// Importing helpers
import mapActivityList from '../../helpers/mapActivityList/mapActivityList';
import { findUnitNumberByLessonId } from '../../helpers/lessonUtils/findUnitNumberByLessonId';

// Importing service
import { saveStudentAnswer, getStudentActivityProgress, getUserLevelProgress } from '../../api/services/TableOfContentService/TableOfContentService';
import { getEndOfLessonScore, saveEndOfLessonCompleted } from '../../api/services/endOfLessonService/endOfLessonService';
import { resetActivity, getReviewLesson } from '../../api/services/reviewService/reviewService';
import getPrintPdfUrl from '../../api/services/printPdfService/printPdfService';
import { getStudentInfo } from '../../api/services/idamApiService/idamApiService';
import { checkUnitEncounterReadiness, getStudentUpcomingClasses } from '../../api/services/lsApiservice/lsApiService';

// Importing context
import NotificationConfigContext from '../../context/NotificationConfig/NotificationConfigContext';
import CornerLoaderContext from '../../context/CornerLoaderContext/CornerLoaderContext';

import { rollbar } from '../RollbarConfig/RollbarConfig';

import { SOMETHING_WENT_WRONG, SSDS_CLASS_TYPES } from '../../constants/CONSTANTS';
import utils from '../../helpers/chatbotUtils/chatbotUtils';

/**
 * Player component
 * @returns {element}
 */
const Player = () => {
  const history = useHistory();
  const notificationContext = useContext(NotificationConfigContext);
  const loaderContext = useContext(CornerLoaderContext);
  const userState = JSON.parse(localStorage.getItem('UserState'));
  const location = useLocation();
  const param = useParams();
  let isEOLCompleted = false;
  const query = new URLSearchParams(location.search);
  const RedirectToDashboard = localStorage.getItem('RedirectToDashboard');
  const {
    lesson, studentId, reviewEnabled, levelid, redirectToDashboard
  } = location.state || {
    lesson: null,
    studentId: userState?.userId || '',
    reviewEnabled: false,
    levelid: query.get('LevelNumber'),
    redirectToDashboard: RedirectToDashboard
  };
  localStorage.removeItem('RedirectToDashboard');
  const [lessonData, setLessonData] = useState(lesson);
  const [isRedirectEnabled, setRedirectStatus] = useState(false);
  const recommendEarlierEncounter = useRef(false);
  const unitData = useRef(null);

  /**
   * To get mapped lesson
   * @param {object} data
   * @returns {object}
   */
  const getMappedLesson = (data) => {
    const standardLesson = data.units[0].lessons[0];
    const activityData = mapActivityList(standardLesson.activityCount, standardLesson.activities);
    standardLesson.activities = activityData;
    return standardLesson;
  };

  /**
   * Function to navigate back to TOC page from player
   */
  const backToTableOfContent = () => {
    if (userState.isStaffToStudent) {
      localStorage.clear();
    }
    isEOLCompleted ? history.replace(`/${studentId}/toc/${levelid}`) : (redirectToDashboard ? (window.location.href = `${process.env.REACT_APP_NSE_DOMAIN_URL}${userState.isStaffToStudent ? `profile/${studentId}/studentView` : 'dashboard'}`) : history.goBack());
  };

  /**
   * To get student activity result
   * @param {string} activityReferenceId
   * @returns {object}
   */
  const getStudentActivityResult = (activityReferenceId) => {
    return getStudentActivityProgress(
      studentId, activityReferenceId, reviewEnabled, notificationContext
    );
  };

  /**
   * To save the student activity result and return promise to close player
   * @param {object} data
   * @returns {object}
   */
  const saveStudentActivityResult = (data) => {
    const activityData = {
      ...data,
      courseContentId: param.id
    };

    return saveStudentAnswer(studentId, activityData, reviewEnabled, notificationContext);
  };

  /**
   * To get student eol score and viewed status
   * @returns {object}
   */
  const getStudentEolScore = () => {
    return getEndOfLessonScore(studentId, param.id, notificationContext, loaderContext);
  };

  /**
   * Function to complete EOL and navigate student back to core course
   */
  const continueCourse = async () => {
    await saveEndOfLessonCompleted(studentId, param.id, notificationContext);
    setRedirectStatus(true);
    const dashboardUrl = new URL(`${process.env.REACT_APP_NSE_DOMAIN_URL}dashboard`);
    if (recommendEarlierEncounter.current) {
      dashboardUrl.searchParams.set('bookEarlierEncounter', '1');
      window.dataLayer?.push({ event: 'book_earlier_encounter_cta_click', user_id: userState.userId });
    }
    window.location.href = dashboardUrl.toString();
  };

  const goBackToDashboard = () => {
    window.location.href = `${process.env.REACT_APP_NSE_DOMAIN_URL}dashboard`;
  };

  /**
   * Function to complete EOL and navigate student back to TOC page
   */
  const backToWorkbooks = async () => {
    isEOLCompleted = true;
    backToTableOfContent();
    return saveEndOfLessonCompleted(studentId, param.id, notificationContext);
  };

  const checkEncounterEarlierRecommendationReadiness = async () => {
    try {
      if (!reviewEnabled && (!userState.isStaffToStudent && !userState.isStaffView)) {
        const currentUnitNumber = findUnitNumberByLessonId(unitData.current, param.id);
        if (currentUnitNumber) {
          const encounterReadiness = await checkUnitEncounterReadiness(studentId, currentUnitNumber);
          if (encounterReadiness) {
            const upcomingClasses = await getStudentUpcomingClasses(studentId);
            const encounterEvent = upcomingClasses?.elements.find(
              (event) => event?.typeId === SSDS_CLASS_TYPES.ENCOUNTER || event?.typeId === SSDS_CLASS_TYPES.ONLINE_ENCOUNTER
            );
            if (encounterEvent?.eventId) {
              const fortyEightHoursFromNow = new Date(Date.now() + 48 * 60 * 60 * 1000);
              const twelveHoursFromNow = new Date(Date.now() + 12 * 60 * 60 * 1000);
              // recommend earlier encounter
              // for ENCOUNTER, when booked class start after 48 hours from now
              // for ONLINE_ENCOUNTER, when booked class start after 12 hours from now
              if ((encounterEvent?.typeId === SSDS_CLASS_TYPES.ENCOUNTER && new Date(encounterEvent?.startDate) < fortyEightHoursFromNow)
                || (encounterEvent?.typeId === SSDS_CLASS_TYPES.ONLINE_ENCOUNTER && new Date(encounterEvent?.startDate) < twelveHoursFromNow)
              ) {
                return false;
              }
            }
            // if no encounter event is booked or it obeys the above condition, then recommend earlier encounter
            return true;
          }
        }
      }
      return false;
    } catch (error) {
      rollbar.error(error);
      return false;
    }
  };

  const shouldRecommendEarlierEncounter = async () => {
    recommendEarlierEncounter.current = await checkEncounterEarlierRecommendationReadiness();
    return recommendEarlierEncounter.current;
  };

  /**
   * To reset review lesson activity
   * @param {string} activityReferenceId
   */
  const resetReviewActivity = (activityReferenceId) => {
    resetActivity(studentId, activityReferenceId, notificationContext);
  };

  /**
   * To generate PDF in new tab
   * @param {string} activityReferenceId
   * @param {string} studentFullName
   */
  const printPdfHandler = (activityReferenceId, studentFullName) => {
    return getPrintPdfUrl(
      studentId,
      studentFullName,
      param.id,
      activityReferenceId,
      reviewEnabled,
      notificationContext,
      loaderContext,
      (userState.isStaffView && !userState.isStaffToStudent)
    ).then((data) => {
      if (data && data.status === 400) {
        rollbar.error(data);
        const tab = window.open('about:blank', '_blank');
        tab.document.write(`<div>${SOMETHING_WENT_WRONG}</div>`);
        tab.document.close();
        return;
      }
      window.open(data.pdf, '_blank');
    });
  };

  /**
   * To generate PDF based on role
   * @param {string} activityReferenceId
   */
  const printPdf = (activityReferenceId) => {
    if (userState.isStaffToStudent) {
      return getStudentInfo(studentId, notificationContext, loaderContext).then((studentInfo) => {
        printPdfHandler(activityReferenceId, studentInfo.fullName);
      });
    }
    return printPdfHandler(activityReferenceId, userState.studentFullName);
  };

  const configOptions = {
    contentUrl: lessonData && lessonData.url,
    domainName: process.env.REACT_APP_ASSET_URL,
    activities: lessonData && lessonData.activities,
    getSavedAnswer: getStudentActivityResult,
    getStudentEolScore,
    continueCourse,
    backToWorkbooks,
    saveStudentActivityResult,
    resetReviewActivity,
    printPdf,
    reviewEnabled,
    isStaffView: (userState?.isStaffView && !userState?.isStaffToStudent) || false,
    isViewOnly: (userState?.isStaffToStudent && userState?.isStaffView) || false,
    isRedirectEnabled,
    setRedirectStatus,
    goBackToDashboard,
    shouldRecommendEarlierEncounter
  };

  /**
   * Function to set the lesson data for review lesson on refresh
   */
  const setReviewLesson = () => {
    getReviewLesson(studentId, param.id, notificationContext, loaderContext)
      .then((response) => {
        const mappedLessonData = getMappedLesson(response);
        setLessonData(mappedLessonData);
      });
  };

  /**
   * Function to set the lesson data for standard lesson on refresh
   */
  const setStandardLesson = () => {
    getUserLevelProgress(studentId, levelid, notificationContext, loaderContext, param.id)
      .then((response) => {
        const mappedLessonData = getMappedLesson(response);
        unitData.current = response?.units;
        setLessonData(mappedLessonData);
      });
  };

  // To updated the lesson on page refresh
  useEffect(() => {
    if (!lessonData) {
      (reviewEnabled) ? setReviewLesson() : setStandardLesson();
    }
  }, [lessonData]);

  // To reset the lesson data on page refresh
  useEffect(() => {
    if (history.location.state && history.location.state.lesson) {
      history.replace(`/student/workbooksplayer/${param.id}`, {
        ...history.location.state,
        lesson: null
      });
    }
  }, []);

  utils.useChatbotClose();

  return useMemo(() => {
    return lessonData && (
      <WorkbooksPlayer
        configOptions={configOptions}
        close={backToTableOfContent}
      />
    );
  }, [lessonData]);
};

export default Player;
